import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Close } from '@mui/icons-material'
import { Box, Chip, IconButton, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material'

import { formatFileSize } from '../../helpers/uploadHelpers'

export type UploadStatus = 'waiting' | 'converting' | 'uploading' | 'uploaded' | 'added' | 'failed' | 'too_big'

export type UploadEntry = {
  key: string
  file: File
  status: UploadStatus
  upload?: { id: string; url: string }
}

const statusColors: Record<UploadStatus, 'default' | 'info' | 'success' | 'error' | 'warning'> = {
  waiting: 'default',
  converting: 'info',
  uploading: 'info',
  uploaded: 'warning',
  added: 'success',
  failed: 'error',
  too_big: 'error',
}

const Thumbnail: FC<{ file: File }> = ({ file }) => {
  const [src, setSrc] = useState<string>()
  useEffect(() => {
    if (!window.URL?.createObjectURL) return
    const url = window.URL.createObjectURL(file)
    setSrc(url)
    return () => window.URL.revokeObjectURL?.(url)
  }, [file])
  return src ? <Box component="img" src={src} alt="" sx={{ maxWidth: 150, maxHeight: 150, display: 'block' }} /> : null
}

type UploadFilesTableProps = {
  entries: UploadEntry[]
  disabled?: boolean
  onRemove: (key: string) => void
}

export const UploadFilesTable: FC<UploadFilesTableProps> = ({ entries, disabled, onRemove }) => {
  const { t } = useTranslation()

  return (
    <Table size="small" aria-label={t('internal-analysts:analysis_screenshots_upload_files')}>
      <TableHead>
        <TableRow>
          <TableCell sx={{ width: 170 }}>{t('internal-analysts:analysis_screenshots_upload_thumbnail')}</TableCell>
          <TableCell>{t('internal-analysts:analysis_screenshots_upload_file_name')}</TableCell>
          <TableCell>{t('internal-analysts:analysis_screenshots_upload_file_size')}</TableCell>
          <TableCell>{t('internal-analysts:analysis_screenshots_upload_status')}</TableCell>
          <TableCell />
        </TableRow>
      </TableHead>
      <TableBody>
        {entries.map((entry) => (
          <TableRow key={entry.key}>
            <TableCell>
              <Thumbnail file={entry.file} />
            </TableCell>
            <TableCell>{entry.file.name}</TableCell>
            <TableCell>{formatFileSize(entry.file.size)}</TableCell>
            <TableCell>
              <Chip size="small" color={statusColors[entry.status]} label={t(`internal-analysts:analysis_screenshots_upload_status_${entry.status}`)} />
            </TableCell>
            <TableCell align="right">
              {(entry.status === 'waiting' || entry.status === 'failed' || entry.status === 'too_big') && (
                <IconButton
                  size="small"
                  disabled={disabled}
                  aria-label={t('internal-analysts:analysis_screenshots_upload_remove', { name: entry.file.name })}
                  onClick={() => onRemove(entry.key)}
                >
                  <Close fontSize="small" />
                </IconButton>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
